import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { aigleColor } from "@styles/colorStyleGuide";
import { AigleButton } from "@components/common/Button/AigleButton";
import { AigleInput } from "@components/common/Input/AigleInput";
import { AigleCheckbox } from "@components/common/Checkbox/AigleCheckbox";

import { ReactComponent as AigleLogo } from "@common/images/aigle_logo.svg";
import { ReactComponent as CheckLogin } from "@common/images/CheckLogin.svg";
import { ReactComponent as CheckLoginOff } from "@common/images/CheckLoginOff.svg";
import { ReactComponent as VisibilityIcon } from "@common/images/VisibilityIcon.svg";
import { ReactComponent as VisibilityOffIcon } from "@common/images/VisibilityOffIcon.svg";
import { ReactComponent as WarningTableIcon } from "@common/images/WarningTableIcon.svg";

import { Typography } from "@mui/material";
import useNaviCustom from "@hooks/useNaviCustom";

import { useStores } from "@hooks/useStores";
import { AuthState } from "@stores/support/AuthState";

type Props = {}

// Styled Components
const Container = styled.div`
	width: 100%;
	min-height: 100vh;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${aigleColor.background.default};
	padding: 40px 20px;
`;

const LoginCard = styled.div`
	width: 100%;
	max-width: 440px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const LogoSection = styled.div`
	margin-bottom: 48px;
	display: flex;
	justify-content: center;
	
	svg {
		width: auto;
		height: 48px;
	}
`;

const InputWrapper = styled.div`
	width: 100%;
	margin-bottom: 12px;
`;

const PasswordToggleButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	
	svg {
		width: 20px;
		height: 20px;
		color: ${aigleColor.grey.dark};
	}
	
	&:hover svg {
		color: ${aigleColor.grey.darker};
	}
`;

const CheckboxContainer = styled.div`
	width: 100%;
	margin-bottom: 24px;
`;

const LoginButtonWrapper = styled.div`
	width: 100%;
	margin-bottom: 24px;
`;

const LinksSection = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 12px;
	margin-bottom: 40px;
`;

const Link = styled(Typography)`
	font-size: 14px;
	color: ${aigleColor.text.secondary};
	cursor: pointer;
	transition: color 0.2s ease;
	
	&:hover {
		color: ${aigleColor.text.primary};
		text-decoration: underline;
	}
`;

const Divider = styled.div`
	width: 1px;
	height: 12px;
	background-color: ${aigleColor.border.default};
`;

const SignUpSection = styled.div`
	width: 100%;
	background-color: ${aigleColor.background.paper};
	border-radius: 8px;
	padding: 32px 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`;

const SignUpTitle = styled(Typography)`
	font-size: 16px;
	font-weight: 600;
	color: ${aigleColor.text.primary};
	text-align: center;
`;

const SignUpDescription = styled(Typography)`
	font-size: 14px;
	color: ${aigleColor.text.secondary};
	text-align: center;
	line-height: 1.6;
`;

const SignUpButtonWrapper = styled.div`
	width: 100%;
`;

const ErrorMessage = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	background-color: ${aigleColor.alpha.black.alpha5};
	border-radius: 4px;
	margin-bottom: 16px;

	svg {
		width: 18px;
		height: 18px;
		color: ${aigleColor.semantic.error};
		flex-shrink: 0;
	}

	.MuiTypography-root {
		font-size: 14px;
		color: ${aigleColor.semantic.error};
	}
`;


function LoginVer2(props: Props) {
	const { authStore, userStore } = useStores();
	const { t: i18next } = useTranslation();
	const naviCustom = useNaviCustom();

	const [showPassword, setShowPassword] = React.useState(false);
	const [rememberLogin, setRememberLogin] = React.useState(false);

	const { login, loginState, loginFail } = authStore;

	React.useEffect(() => {
		return () => {
			authStore.initLoginFails();
		}
	}, [authStore]);

	const handleClickShowPassword = () => setShowPassword(!showPassword);
	const handleMouseDownPassword = (event: React.MouseEvent) => event.preventDefault();
	const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => authStore.changeLoginEmail(event.target.value);
	const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => authStore.changeLoginPassword(event.target.value);
	const handleKeypress = (event: React.KeyboardEvent) => { if (event.key === 'Enter') { handleClickLogin(); } };
	const handleClickLogin = () => {
		authStore.doLogin(loginCallback);
		naviCustom.go(`/projects`);
	};

	const loginCallback = () => {
		if (loginState === AuthState.Authenticated) {
			userStore.getUserProfile(authStore.loginUser.code);
		}
	};

	const handleClickSignUp = () => {
		naviCustom.go('/signup');
	};

	const handleClickFindId = () => {
		// TODO: 아이디 찾기 페이지로 이동
		console.log('아이디 찾기');
	};

	const handleClickFindPassword = () => {
		naviCustom.go('/forgot-password');
	};


	// 로그인 컴포넌트
	const loginComponent = () => {
		return (
			<LoginCard>
				{/* 로고 */}
				<LogoSection>
					<AigleLogo />
				</LogoSection>

				{/* 아이디 입력 */}
				<InputWrapper>
					<AigleInput
						label={i18next("아이디")}
						floatingLabel
						placeholder={i18next("아이디")}
						value={login.email}
						onChange={handleChangeEmail}
					/>
				</InputWrapper>

				{/* 비밀번호 입력 */}
				<InputWrapper>
					<AigleInput
						label={i18next("비밀번호")}
						floatingLabel
						placeholder={i18next("비밀번호")}
						value={login.password}
						onChange={handleChangePassword}
						onKeyPress={handleKeypress}
						type={showPassword ? 'text' : 'password'}
						endAdornment={
							<PasswordToggleButton
								onClick={handleClickShowPassword}
								onMouseDown={handleMouseDownPassword}
								type="button"
							>
								{!showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
							</PasswordToggleButton>
						}
					/>
				</InputWrapper>

			{/* 로그인 저장 체크박스 */}
			<CheckboxContainer>
				<AigleCheckbox
					label="로그인 저장"
					checked={rememberLogin}
					onChange={(checked) => setRememberLogin(checked)}
				/>
			</CheckboxContainer>

				{/* 에러 메시지 */}
				{loginFail && (
					<ErrorMessage>
						<WarningTableIcon />
						<Typography>{i18next("로그인에 실패했습니다. 다시 시도해주세요.")}</Typography>
					</ErrorMessage>
				)}

				{/* 로그인 버튼 */}
				<LoginButtonWrapper>
					<AigleButton
						variant="contained"
						color="primary"
						size="medium"
						fullWidth
						onClick={handleClickLogin}
						disabled={!(login.email?.length > 0 && login.password?.length > 0)}
					>
						{i18next("로그인")}
					</AigleButton>
				</LoginButtonWrapper>

				{/* 아이디 찾기 | 비밀번호 찾기 */}
				<LinksSection>
					<Link onClick={handleClickFindId}>{i18next("아이디 찾기")}</Link>
					<Divider />
					<Link onClick={handleClickFindPassword}>{i18next("비밀번호 찾기")}</Link>
				</LinksSection>

				{/* 회원가입 섹션 */}
				<SignUpSection>
					<SignUpTitle>{i18next("아직 회원이 아니신가요?")}</SignUpTitle>
					<SignUpDescription>
						{i18next("아이글과 함께 편리한 서논술 채점 관리를 시작해 보세요!")}
					</SignUpDescription>
					<SignUpButtonWrapper>
						<AigleButton
							variant="outlined"
							color="primary"
							size="medium"
							fullWidth
							onClick={handleClickSignUp}
						>
							{i18next("회원가입")}
						</AigleButton>
					</SignUpButtonWrapper>
				</SignUpSection>
			</LoginCard>
		);
	};

	return (
		<Container>
			{loginComponent()}
		</Container>
	);
}

export default observer<typeof LoginVer2>(LoginVer2);
